// Package imports:
import React, { Fragment, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSearch } from '@fortawesome/pro-solid-svg-icons';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { ErrorMessages } from '../../../services/errorMessages';
import { isElementSubChild } from '../../../services/utils';
// Type imports:
import { ICompanySearchResults, ICompanySearchResultsItem } from '../../../types/SearchTypes';

const MINIMUM_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 300;

interface IProps {
    onSelect: (item: ICompanySearchResultsItem) => void;
    placeholder?: string;
    size?: 'sm' | 'lg';
    disabled?: boolean;
    id?: string;
}

type FetchState =
    | { status: 'idle' }
    | { status: 'loading' }
    | { status: 'success'; results: ICompanySearchResultsItem[] }
    | { status: 'error'; message: string };

const CompanyTypeahead: React.FC<IProps> = ({
    onSelect,
    placeholder = 'Leitaðu eftir fyrirtæki',
    size = 'lg',
    disabled,
    id,
}) => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [fetchState, setFetchState] = useState<FetchState>({ status: 'idle' });
    const [isFocused, setIsFocused] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const listboxId = useRef(id ?? `company-typeahead-${Math.random().toString(36).slice(2, 8)}`);

    // Debounce: only update debouncedQuery DEBOUNCE_MS after the user stops typing
    useEffect(() => {
        const trimmed = query.trim();
        if (trimmed.length < MINIMUM_SEARCH_LENGTH) {
            setDebouncedQuery('');
            return;
        }
        const id = window.setTimeout(() => setDebouncedQuery(trimmed), DEBOUNCE_MS);
        return () => window.clearTimeout(id);
    }, [query]);

    // Fetch results for the debounced query. AbortController prevents race conditions
    // by cancelling any in-flight request when a newer query arrives.
    useEffect(() => {
        if (debouncedQuery.length < MINIMUM_SEARCH_LENGTH) {
            setFetchState({ status: 'idle' });
            return;
        }

        const controller = new AbortController();
        setFetchState({ status: 'loading' });

        (async () => {
            try {
                const url = `${GET_KELDAN_API_URL()}/Company/SearchRskConnector?searchTerm=${encodeURIComponent(debouncedQuery)}`;
                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) {
                    setFetchState({ status: 'error', message: ErrorMessages.RequestFailed });
                    return;
                }
                const body: ICompanySearchResults = await response.json();
                const results = (body.results ?? []).filter(
                    (item): item is ICompanySearchResultsItem & { ssn: string; name: string } =>
                        item.ssn !== null && item.name !== null
                );
                setFetchState({ status: 'success', results });
            } catch (e) {
                if (e instanceof DOMException && e.name === 'AbortError') return;
                setFetchState({ status: 'error', message: ErrorMessages.NetworkError });
            }
        })();

        return () => controller.abort();
    }, [debouncedQuery]);

    // Close dropdown when clicking outside the component
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            const target = e.target;
            const container = containerRef.current;
            if (!container) return;
            if (target instanceof Element && isElementSubChild(target, container)) return;
            setIsFocused(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const results = fetchState.status === 'success' ? fetchState.results : [];
    const isOpen = isFocused && debouncedQuery.length >= MINIMUM_SEARCH_LENGTH;

    // Reset/clamp the highlighted index when the result set changes
    useEffect(() => {
        setHighlightedIndex(results.length > 0 ? 0 : -1);
    }, [debouncedQuery, results.length]);

    // Keep the highlighted item visible while navigating with the keyboard
    useEffect(() => {
        if (highlightedIndex < 0) return;
        itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    const handleSelect = (item: ICompanySearchResultsItem) => {
        onSelect(item);
        setQuery('');
        setDebouncedQuery('');
        setFetchState({ status: 'idle' });
        setIsFocused(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsFocused(false);
            return;
        }
        if (!isOpen || results.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(i => (i + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(i => (i <= 0 ? results.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0 && highlightedIndex < results.length) {
                e.preventDefault();
                handleSelect(results[highlightedIndex]);
            }
        }
    };

    // Render text with the matched substring(s) wrapped in <mark>, using React nodes (no innerHTML)
    const renderHighlighted = (text: string, term: string) => {
        if (!term) return text;
        const lowerText = text.toLowerCase();
        const lowerTerm = term.toLowerCase();
        const nodes: React.ReactNode[] = [];
        let cursor = 0;
        let key = 0;
        while (cursor < text.length) {
            const idx = lowerText.indexOf(lowerTerm, cursor);
            if (idx === -1) {
                nodes.push(<Fragment key={key++}>{text.slice(cursor)}</Fragment>);
                break;
            }
            if (idx > cursor) nodes.push(<Fragment key={key++}>{text.slice(cursor, idx)}</Fragment>);
            nodes.push(<mark key={key++}>{text.slice(idx, idx + term.length)}</mark>);
            cursor = idx + term.length;
        }
        return nodes;
    };

    const renderDropdownContent = () => {
        if (fetchState.status === 'loading') {
            return <div className="typeahead__status">Leita...</div>;
        }
        if (fetchState.status === 'error') {
            return (
                <div className="typeahead__status typeahead__status--error">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="icon" />
                    {fetchState.message}
                </div>
            );
        }
        if (fetchState.status === 'success' && results.length === 0) {
            return <div className="typeahead__status">Engar niðurstöður fundust</div>;
        }
        return results.map((item, idx) => (
            <button
                key={item.ssn ?? idx}
                type="button"
                role="option"
                aria-selected={idx === highlightedIndex}
                className={cx('typeahead__option', { 'is-highlighted': idx === highlightedIndex })}
                ref={el => { itemRefs.current[idx] = el; }}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={e => e.preventDefault()}  // keep focus on input so blur doesn't close before click fires
                onClick={() => handleSelect(item)}
            >
                {renderHighlighted(`${item.name} Kt. ${item.ssn}`, debouncedQuery)}
            </button>
        ));
    };

    return (
        <div
            ref={containerRef}
            className={cx('KCL_company-typeahead', size, {
                'is-open': isOpen,
                'is-disabled': disabled,
            })}
        >
            <div className="typeahead__field">
                <span className="typeahead__icon">
                    <FontAwesomeIcon icon={faSearch} />
                </span>
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listboxId.current}
                    aria-autocomplete="list"
                    aria-activedescendant={highlightedIndex >= 0 ? `${listboxId.current}-${highlightedIndex}` : undefined}
                    autoComplete="off"
                    className="typeahead__input"
                    placeholder={placeholder}
                    disabled={disabled}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={handleKeyDown}
                />
            </div>
            <div
                id={listboxId.current}
                role="listbox"
                className={cx('typeahead__dropdown', { 'is-open': isOpen })}
            >
                {renderDropdownContent()}
            </div>
        </div>
    );
};

export default CompanyTypeahead;
